import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { ArrowLeft, Copy, Download, Plus, RefreshCw, Users } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fmt = (v) => (v === null || v === undefined ? "—" : Number(v).toFixed(2));
const copyText = async (t, label) => {
  try { await navigator.clipboard.writeText(t); toast.success(`${label} copiado`); }
  catch (_) {
    try {
      const ta = document.createElement("textarea");
      ta.value = t; document.body.appendChild(ta); ta.select();
      document.execCommand("copy"); document.body.removeChild(ta);
      toast.success(`${label} copiado`);
    } catch (__) { toast.error("No se pudo copiar"); }
  }
};

const EMPTY = { name: "", contact: "", country: "PE", code: "", commission_pct: 10, language: "es" };
const inputCls = "w-full bg-[#0B2545] border border-white/15 rounded-lg px-3 py-2 text-white text-sm";

// CORTE 5-C — Master admin de partners (todo en español)
export default function AdminPartners() {
  const [partners, setPartners] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [showForm, setShowForm] = useState(false);
  const [createdLinks, setCreatedLinks] = useState(null);
  const [detail, setDetail] = useState(null);
  const [payNote, setPayNote] = useState("");
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try { const r = await axios.get(`${API}/admin/partners`); setPartners(r.data.partners); }
    catch (_) { toast.error("Error cargando partners"); }
  };
  useEffect(() => { load(); }, []);

  const openDetail = async (pid) => {
    try { const r = await axios.get(`${API}/admin/partners/${pid}`); setDetail(r.data); }
    catch (_) { toast.error("Error cargando ficha"); }
  };

  const create = async () => {
    setSaving(true);
    try {
      const r = await axios.post(`${API}/admin/partners`, { ...form, commission_pct: Number(form.commission_pct) });
      setCreatedLinks(r.data.links);
      setForm(EMPTY); setShowForm(false);
      toast.success("Partner creado");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail === "code_exists" ? "Ese código ya existe" : "Error al crear");
    } finally { setSaving(false); }
  };

  const markPaid = async (cid) => {
    try {
      await axios.post(`${API}/admin/commissions/${cid}/mark-paid`, { note: payNote });
      toast.success("Comisión marcada como pagada — se refleja al instante en la vista del partner");
      setPayNote("");
      openDetail(detail.partner.id); load();
    } catch (e) { toast.error(e.response?.data?.detail === "already_paid" ? "Ya estaba pagada" : "Error"); }
  };

  const revokeToken = async (pid) => {
    if (!window.confirm("¿Revocar el token? El link actual del partner quedará inservible al instante.")) return;
    try {
      const r = await axios.post(`${API}/admin/partners/${pid}/revoke-token`);
      toast.success("Token revocado. Nuevo link generado.");
      copyText(r.data.view_link, "Nuevo link de vista");
      openDetail(pid); load();
    } catch (_) { toast.error("Error al revocar"); }
  };

  // Reversión protegida: obliga a escribir un motivo antes de ejecutar
  const reverse = async (entry) => {
    const motivo = window.prompt(
      `REVERTIR comisión ${entry.tx_id || ""} (${entry.commission_amount} ${entry.currency}).\n` +
      "Se creará un asiento NEGATIVO — el original no se toca.\n\n" +
      "Escribe el MOTIVO (obligatorio, ej: contracargo del 25/07):");
    if (motivo === null) return;
    if (!motivo.trim() || motivo.trim().length < 5) {
      toast.error("El motivo es obligatorio (mínimo 5 caracteres). No se revirtió nada.");
      return;
    }
    try {
      await axios.post(`${API}/admin/commissions/${entry.id}/reverse`, { note: motivo.trim() });
      toast.success("Reversión asentada (asiento negativo)");
      openDetail(detail.partner.id); load();
    } catch (e) {
      toast.error(e.response?.data?.detail === "cannot_reverse" ? "Ya estaba revertida" : "Error al revertir");
    }
  };

  const updatePartner = async (pid, updates) => {
    try { await axios.put(`${API}/admin/partners/${pid}`, updates); openDetail(pid); load(); toast.success("Guardado"); }
    catch (_) { toast.error("Error al guardar"); }
  };

  const LinksBlock = ({ links }) => (
    <div className="space-y-2" data-testid="partner-links-block">
      {[["Código", links.code], ["Link de referido", links.ref_link], ["Link de la vista", links.view_link]].map(([label, val]) => (
        <div key={label} className="flex items-center gap-2">
          <span className="text-[11px] text-slate-500 w-28 shrink-0">{label}</span>
          <code className="bg-[#071B2F] border border-white/15 rounded px-2 py-1 text-xs flex-1 truncate text-amber-300">{val}</code>
          <button onClick={() => copyText(val, label)} className="p-1.5 bg-[#14375F] hover:bg-slate-700 rounded" data-testid={`copy-${label.toLowerCase().replace(/ /g, "-")}`}>
            <Copy className="w-3.5 h-3.5 text-white" />
          </button>
        </div>
      ))}
    </div>
  );

  return (
    <div className="min-h-screen bg-[#0B2545] p-4 sm:p-8" data-testid="admin-partners-page">
      <div className="max-w-5xl mx-auto">
        <Link to="/admin" className="inline-flex items-center gap-2 text-slate-400 hover:text-white mb-6 text-sm" data-testid="partners-back-link">
          <ArrowLeft className="w-4 h-4" /> Volver al panel
        </Link>
        <div className="flex items-center justify-between mb-6">
          <h1 className="text-2xl font-bold text-white flex items-center gap-2"><Users className="w-6 h-6 text-amber-400" /> Partners</h1>
          <Button onClick={() => setShowForm(!showForm)} className="bg-amber-600 hover:bg-amber-500 rounded-full" data-testid="partner-new-btn">
            <Plus className="w-4 h-4 mr-1" /> Nuevo partner
          </Button>
        </div>

        {showForm && (
          <div className="bg-[#14375F]/60 border border-white/15 rounded-2xl p-5 mb-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="partner-form">
            <div><p className="text-xs text-slate-400 mb-1">Nombre</p>
              <input className={inputCls} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="partner-form-name" /></div>
            <div><p className="text-xs text-slate-400 mb-1">Contacto (email para avisos)</p>
              <input className={inputCls} value={form.contact} onChange={(e) => setForm({ ...form, contact: e.target.value })} placeholder="partner@email.com" data-testid="partner-form-contact" /></div>
            <div><p className="text-xs text-slate-400 mb-1">País (ISO)</p>
              <input className={inputCls + " uppercase"} maxLength={2} value={form.country} onChange={(e) => setForm({ ...form, country: e.target.value })} data-testid="partner-form-country" /></div>
            <div><p className="text-xs text-slate-400 mb-1">Código (= cupón)</p>
              <input className={inputCls + " font-mono uppercase"} value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })} data-testid="partner-form-code" /></div>
            <div><p className="text-xs text-slate-400 mb-1">% comisión</p>
              <input type="number" min="0" max="50" className={inputCls} value={form.commission_pct} onChange={(e) => setForm({ ...form, commission_pct: e.target.value })} data-testid="partner-form-pct" /></div>
            <div><p className="text-xs text-slate-400 mb-1">Idioma de avisos</p>
              <select className={inputCls} value={form.language} onChange={(e) => setForm({ ...form, language: e.target.value })} data-testid="partner-form-language">
                <option value="es">Español</option><option value="pt">Portugués</option>
                <option value="en">Inglés</option><option value="fr">Francés</option>
              </select></div>
            <div className="sm:col-span-2 lg:col-span-3">
              <Button onClick={create} disabled={saving || !form.name.trim() || !form.code.trim()} className="bg-[#1B84FF] hover:bg-[#1B84FF] rounded-full" data-testid="partner-create-btn">
                {saving ? "Creando…" : "Crear partner"}
              </Button>
            </div>
          </div>
        )}

        {createdLinks && (
          <div className="bg-[#1B84FF]/10 border border-emerald-500/40 rounded-2xl p-5 mb-6" data-testid="partner-created-links">
            <p className="text-emerald-300 text-sm font-semibold mb-3">Partner creado — listo para copiar:</p>
            <LinksBlock links={createdLinks} />
          </div>
        )}

        {/* Lista con rendimiento */}
        {!partners ? <p className="text-slate-500">Cargando…</p> : (
          <div className="space-y-3" data-testid="partners-list">
            {partners.map((p) => (
              <div key={p.id} className="bg-[#14375F]/60 border border-white/15 rounded-xl p-4" data-testid={`partner-row-${p.code}`}>
                <div className="flex items-center gap-3 flex-wrap">
                  <button onClick={() => openDetail(p.id)} className="font-semibold text-white hover:text-amber-300" data-testid={`partner-open-${p.code}`}>{p.name}</button>
                  <code className="text-xs text-amber-300 font-mono">{p.code}</code>
                  <Badge className={p.status === "active" ? "bg-[#1B84FF]/20 text-emerald-300" : "bg-slate-500/20 text-slate-400"}>{p.status === "active" ? "activo" : "inactivo"}</Badge>
                  <span className="text-xs text-slate-400">{p.commission_pct}% comisión</span>
                  <span className="text-xs text-slate-500 ml-auto">
                    {p.performance.funnel.visits} visitas · {p.performance.funnel.registrations} registros · {p.performance.redemptions} canjes
                  </span>
                </div>
                {Object.keys(p.performance.by_currency).length > 0 && (
                  <div className="flex flex-wrap gap-2 mt-2">
                    {Object.entries(p.performance.by_currency).map(([cur, t]) => (
                      <span key={cur} className="text-[11px] px-2 py-1 rounded bg-[#0B2545] border border-white/15 text-slate-300">
                        {cur}: ingresos {fmt(t.revenue)} · adeudada <span className="text-sky-300">{fmt(t.owed)}</span> · pagada <span className="text-emerald-300">{fmt(t.paid)}</span>
                      </span>
                    ))}
                  </div>
                )}
              </div>
            ))}
            {partners.length === 0 && <p className="text-slate-500 italic">Sin partners todavía.</p>}
          </div>
        )}

        {/* Ficha */}
        {detail && (
          <div className="fixed inset-0 bg-black/60 z-50 flex items-start justify-center overflow-y-auto p-4" onClick={() => setDetail(null)}>
            <div className="bg-[#0B2545] border border-white/15 rounded-2xl p-6 max-w-3xl w-full my-8" onClick={(e) => e.stopPropagation()} data-testid="partner-detail-modal">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-bold text-white">{detail.partner.name}</h2>
                <button onClick={() => setDetail(null)} className="text-slate-400 hover:text-white text-2xl leading-none" data-testid="partner-detail-close">×</button>
              </div>
              <LinksBlock links={detail.links} />

              <div className="grid sm:grid-cols-2 gap-4 mt-4">
                <div>
                  <p className="text-xs text-slate-400 mb-1">% de comisión (se guarda historial)</p>
                  <div className="flex gap-2">
                    <input type="number" min="0" max="50" defaultValue={detail.partner.commission_pct} id="pct-edit" className={inputCls} data-testid="partner-pct-input" />
                    <Button onClick={() => updatePartner(detail.partner.id, { commission_pct: Number(document.getElementById("pct-edit").value) })}
                      className="bg-sky-600 hover:bg-sky-500 text-xs" data-testid="partner-pct-save">Guardar</Button>
                  </div>
                  <p className="text-[10px] text-slate-500 mt-1">
                    Historial: {(detail.partner.pct_history || []).map((h) => `${h.pct}% (${(h.changed_at || "").slice(0, 10)})`).join(" → ")}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-slate-400 mb-1">Avisos por email ({detail.partner.language})</p>
                  <div className="flex gap-3 text-xs text-slate-300">
                    {["sale", "paid", "weekly"].map((k) => (
                      <label key={k} className="flex items-center gap-1">
                        <input type="checkbox" checked={(detail.partner.notify || {})[k] !== false}
                          onChange={(e) => updatePartner(detail.partner.id, { notify: { ...(detail.partner.notify || {}), [k]: e.target.checked } })}
                          data-testid={`partner-notify-${k}`} />
                        {{ sale: "venta", paid: "pago", weekly: "semanal" }[k]}
                      </label>
                    ))}
                  </div>
                  <div className="flex gap-2 mt-3">
                    <Button onClick={() => revokeToken(detail.partner.id)} className="bg-rose-700 hover:bg-rose-600 text-xs" data-testid="partner-revoke-btn">Revocar token</Button>
                    <a href={`${API}/admin/partners/${detail.partner.id}/export.csv`}>
                      <Button className="bg-slate-700 hover:bg-slate-600 text-xs" data-testid="partner-admin-csv-btn"><Download className="w-3.5 h-3.5 mr-1" /> CSV</Button>
                    </a>
                  </div>
                </div>
              </div>

              <h3 className="text-sm font-semibold text-white mt-5 mb-2">Libro de comisiones (append-only)</h3>
              <div className="flex gap-2 mb-2">
                <input className={inputCls} placeholder="Nota del pago (ej: Yape 25/07)" value={payNote} onChange={(e) => setPayNote(e.target.value)} data-testid="partner-pay-note" />
              </div>
              <div className="max-h-72 overflow-y-auto space-y-1.5" data-testid="commission-book">
                {(detail.commission_book || []).length === 0 && <p className="text-slate-500 text-xs italic">Sin asientos todavía.</p>}
                {(detail.commission_book || []).map((e) => (
                  <div key={e.id} className="flex items-center gap-2 text-xs bg-[#071B2F]/60 rounded px-2 py-1.5 flex-wrap" data-testid={`commission-row-${e.tx_id || e.id}`}>
                    <span className="font-mono text-amber-300">{e.tx_id || "—"}</span>
                    <span className={e.entry_type === "reversal" ? "text-rose-300" : "text-slate-300"}>{e.entry_type === "reversal" ? "reversión" : "venta"}</span>
                    <span className="text-slate-400">{fmt(e.amount_charged)} {e.currency}</span>
                    <span className="text-white font-semibold">→ {fmt(e.commission_amount)} {e.currency} ({e.pct_snapshot}%)</span>
                    <span className={e.status === "paid" ? "text-emerald-300" : e.status === "pending" ? "text-sky-300" : "text-rose-300"}>
                      {{ pending: "pendiente", paid: `pagada ${(e.paid_at || "").slice(0, 10)}`, reversal: "reversión" }[e.status] || e.status}
                    </span>
                    {e.paid_note && <span className="text-slate-500">“{e.paid_note}”</span>}
                    <span className="ml-auto text-slate-600">{(e.created_at || "").slice(0, 10)}</span>
                    {e.entry_type === "earn" && e.status === "pending" && (
                      <Button onClick={() => markPaid(e.id)} className="bg-emerald-700 hover:bg-[#0F6FDB] h-6 text-[10px] px-2" data-testid={`mark-paid-${e.tx_id || e.id}`}>
                        Marcar pagada
                      </Button>
                    )}
                    {e.entry_type === "earn" && e.status !== "reversal" && (
                      <Button onClick={() => reverse(e)} className="bg-rose-800 hover:bg-rose-700 h-6 text-[10px] px-2" data-testid={`reverse-${e.tx_id || e.id}`}>
                        Revertir
                      </Button>
                    )}
                  </div>
                ))}
              </div>
              <Button onClick={() => openDetail(detail.partner.id)} className="mt-3 bg-slate-700 hover:bg-slate-600 h-8 text-xs" data-testid="partner-detail-refresh">
                <RefreshCw className="w-3 h-3 mr-1" /> Refrescar
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
